import { CloseIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Link,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import { useExperienceManager } from '@hooks'
import ReplayOutlinedIcon from '@material-ui/icons/ReplayOutlined'
import { useStoreState } from 'easy-peasy'
import {
  Field,
  type FieldProps,
  Form,
  Formik,
  type FormikHelpers
} from 'formik'
import React, { useEffect, useState } from 'react'
import type { UpdateAutoReplenishDto } from 'src/core/api/__generated__/clinician-api/models/updateAutoReplenishDto'
import type { StoreModel } from 'src/store/types'
import * as Yup from 'yup'
import {
  useOrganizationControllerGetSessionCredit,
  useOrganizationControllerUpdateAutoReplenishSettings
} from '~/clinician-api'

interface AutoReplenishProps {
  sessionCount?: number
  propAutoReplenishEnabled?: boolean
  propAutoReplenishThreshold?: number | null
  propAutoReplenishCount?: number | null
  onAutoReplenishChange?: (
    enabled: boolean,
    threshold: number | null,
    replenishCount: number | null
  ) => void
}

interface FormValues {
  autoReplenishEnabled: boolean
  threshold: number | null
  replenishCount: number | null
}

const validationSchema = Yup.object().shape({
  autoReplenishEnabled: Yup.boolean(),
  threshold: Yup.number().when(
    'autoReplenishEnabled',
    (autoReplenishEnabled, schema) => {
      return autoReplenishEnabled
        ? schema
            .min(1, 'Threshold must be at least 1')
            .max(100, 'Threshold cannot exceed 100')
            .required('Threshold is required')
        : schema.nullable()
    }
  ),
  replenishCount: Yup.number().when(
    'autoReplenishEnabled',
    (autoReplenishEnabled, schema) => {
      return autoReplenishEnabled
        ? schema
            .min(30, 'Minimum 30 sessions required for auto-replenish')
            .required('Replenish amount is required')
        : schema.nullable()
    }
  )
})

export const AutoReplenish: React.FC<AutoReplenishProps> = ({
  sessionCount,
  propAutoReplenishEnabled,
  propAutoReplenishThreshold,
  propAutoReplenishCount,
  onAutoReplenishChange
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [autoReplenish, setAutoReplenish] = useState(false)
  const [autoReplenishThreshold, setAutoReplenishThreshold] = useState<
    number | null
  >(null)
  const [autoReplenishCount, setAutoReplenishCount] = useState<number | null>(
    null
  )

  const { user } = useStoreState((state: StoreModel) => state.auth)
  const { usageBasedPricingEnabled } = useExperienceManager()
  const toast = useToast()

  const { data: sessionCredits } = useOrganizationControllerGetSessionCredit(
    user?.clinic?.organization_id || '',
    {
      query: {
        enabled: !!(usageBasedPricingEnabled && user?.clinic?.organization_id)
      }
    }
  )

  const { mutateAsync: updateAutoReplenish } =
    useOrganizationControllerUpdateAutoReplenishSettings()

  const handleSettingsUpdate = (
    enabled: boolean,
    threshold: number | null,
    replenishCount: number | null
  ) => {
    setAutoReplenish(enabled)
    setAutoReplenishThreshold(threshold)
    setAutoReplenishCount(replenishCount)
    onAutoReplenishChange?.(enabled, threshold, replenishCount)
  }

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      // Check if user is on free plan
      const isOnFreePlan =
        !sessionCredits || sessionCredits?.planType === 'free'

      if (isOnFreePlan) {
        // For free plan users, just update local state and call the callback
        handleSettingsUpdate(
          values.autoReplenishEnabled,
          values.autoReplenishEnabled ? values.threshold : null,
          values.autoReplenishEnabled ? values.replenishCount : null
        )

        toast({
          title: 'Auto-replenish settings configured',
          description: 'Settings will be applied when you purchase sessions',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'solid',
          containerStyle: {
            background: '#0D8443',
            borderRadius: '10px',
            color: 'white'
          }
        })

        setSubmitting(false)
        setIsSettingsOpen(false)
        return
      }

      // For paid plan users, save to API
      const updateData: UpdateAutoReplenishDto = {}

      if (values.autoReplenishEnabled) {
        updateData.autoReplenishEnabled = true
        updateData.autoReplenishThreshold = values.threshold
        updateData.autoReplenishCount = values.replenishCount
      } else {
        updateData.autoReplenishEnabled = false
      }

      await updateAutoReplenish({
        organizationId: user?.clinic?.organization_id || '',
        data: updateData
      })

      toast({
        title: 'Auto-replenish settings updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'solid',
        containerStyle: {
          background: '#0D8443',
          borderRadius: '10px',
          color: 'white'
        }
      })

      handleSettingsUpdate(
        values.autoReplenishEnabled,
        values.autoReplenishEnabled ? values.threshold : null,
        values.autoReplenishEnabled ? values.replenishCount : null
      )

      setSubmitting(false)
      setIsSettingsOpen(false)
    } catch (error) {
      toast({
        title: 'Error updating settings',
        description:
          (error as Error)?.message ||
          'Failed to update auto-replenish settings',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      setSubmitting(false)
    }
  }

  const initialValues: FormValues = {
    autoReplenishEnabled: autoReplenish,
    threshold: autoReplenishThreshold ?? 5,
    replenishCount: autoReplenishCount ?? 30
  }
  useEffect(() => {
    setAutoReplenish(propAutoReplenishEnabled ?? false)
    setAutoReplenishThreshold(propAutoReplenishThreshold ?? null)
    setAutoReplenishCount(propAutoReplenishCount ?? null)
  }, [
    propAutoReplenishEnabled,
    propAutoReplenishThreshold,
    propAutoReplenishCount
  ])

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        mt="6"
        p="4"
        bg="gray.50"
        borderRadius="md"
      >
        <HStack>
          <ReplayOutlinedIcon />
          {!sessionCount && !autoReplenish && !sessionCredits ? (
            <Text fontSize="16px" fontWeight="600">
              Auto-Replenish
            </Text>
          ) : (
            <>
              <Text fontSize="16px" fontWeight="600">
                Auto-Replenish
                {/* This accounts for the free trial case */}
                {autoReplenish
                  ? ': ON'
                  : sessionCredits && sessionCredits.planType !== 'free'
                    ? ': OFF'
                    : ''}
              </Text>
              {autoReplenish &&
                autoReplenishCount &&
                autoReplenishThreshold && (
                  <Text fontSize="14px" color="gray.600">
                    Purchase {autoReplenishCount} sessions when you have{' '}
                    {autoReplenishThreshold} left
                  </Text>
                )}
            </>
          )}
        </HStack>
        <Link
          onClick={() => setIsSettingsOpen(true)}
          color="primary"
          fontWeight="normal"
          cursor="pointer"
        >
          Edit
        </Link>
      </Flex>

      {/* Settings Modal */}
      {isSettingsOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.600"
          zIndex={1000}
          onClick={() => setIsSettingsOpen(false)}
        >
          <Flex justify="center" align="center" h="100%">
            <Box
              bg="white"
              borderRadius="lg"
              p="6"
              m="4"
              maxW="500px"
              w="full"
              onClick={e => e.stopPropagation()}
              boxShadow="xl"
              position="relative"
            >
              {/* Close button */}
              <IconButton
                aria-label="Close"
                icon={<CloseIcon fill="#6b7280" />}
                size="sm"
                variant="ghost"
                position="absolute"
                top="4"
                right="4"
                onClick={() => setIsSettingsOpen(false)}
              />

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
              >
                {({ values, errors, touched, isSubmitting }) => (
                  <Form>
                    <VStack spacing="6" align="stretch">
                      <Text fontSize="20px" fontWeight="600" color="gray.900">
                        Auto-Replenish Settings
                      </Text>

                      <Field name="autoReplenishEnabled">
                        {({ field }: FieldProps) => (
                          <FormControl>
                            <Flex justify="space-between" align="center">
                              <Box>
                                <FormLabel
                                  mb="0"
                                  fontSize="16px"
                                  fontWeight="600"
                                  color="gray.900"
                                >
                                  Enable Auto-Replenish
                                </FormLabel>
                                <Text fontSize="14px" color="gray.600" mt="1">
                                  Automatically purchase more sessions when
                                  you're running low
                                </Text>
                              </Box>
                              <Switch
                                {...field}
                                isChecked={field.value}
                                colorScheme="blue"
                                size="lg"
                              />
                            </Flex>
                          </FormControl>
                        )}
                      </Field>

                      {values.autoReplenishEnabled && (
                        <VStack
                          spacing="6"
                          align="stretch"
                          p={4}
                          bg="#F6F7FE"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <Field name="threshold">
                            {({ field }: FieldProps) => (
                              <FormControl
                                w="full"
                                isInvalid={
                                  !!(errors.threshold && touched.threshold)
                                }
                              >
                                <FormLabel
                                  fontSize="14px"
                                  fontWeight="500"
                                  mb="2"
                                  color="gray.700"
                                >
                                  Trigger threshold:
                                </FormLabel>
                                <NumberInput
                                  {...field}
                                  value={field.value || ''}
                                  onChange={valueString =>
                                    field.onChange({
                                      target: {
                                        name: field.name,
                                        value: valueString
                                          ? parseInt(valueString)
                                          : null
                                      }
                                    })
                                  }
                                  min={0}
                                  bg="white"
                                >
                                  <NumberInputField
                                    placeholder="5"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ borderColor: 'gray.300' }}
                                    _focus={{
                                      borderColor: 'blue.500',
                                      bg: 'white'
                                    }}
                                  />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <Text fontSize="14px" color="gray.500" mt="2">
                                  Purchase more sessions when you have this many
                                  left
                                </Text>
                                <FormErrorMessage>
                                  {errors.threshold}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>

                          <Field name="replenishCount">
                            {({ field }: FieldProps) => (
                              <FormControl
                                w="full"
                                isInvalid={
                                  !!(
                                    errors.replenishCount &&
                                    touched.replenishCount
                                  )
                                }
                              >
                                <FormLabel
                                  fontSize="14px"
                                  fontWeight="500"
                                  mb="2"
                                  color="gray.700"
                                >
                                  Replenish amount:
                                </FormLabel>
                                <NumberInput
                                  {...field}
                                  value={field.value || ''}
                                  onChange={valueString =>
                                    field.onChange({
                                      target: {
                                        name: field.name,
                                        value: valueString
                                          ? parseInt(valueString)
                                          : null
                                      }
                                    })
                                  }
                                  min={0}
                                  bg="white"
                                >
                                  <NumberInputField
                                    placeholder="30"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ borderColor: 'gray.300' }}
                                    _focus={{
                                      borderColor: 'blue.500',
                                      bg: 'white'
                                    }}
                                  />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <Text fontSize="14px" color="gray.500" mt="2">
                                  Number of sessions to purchase automatically
                                  (minimum 30 sessions required)
                                </Text>
                                <FormErrorMessage>
                                  {errors.replenishCount}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </VStack>
                      )}

                      <HStack spacing="3" justify="flex-end" pt="4">
                        <Button
                          variant="ghost"
                          onClick={() => setIsSettingsOpen(false)}
                          fontSize="14px"
                          color="gray.600"
                        >
                          Cancel
                        </Button>
                        <Button
                          bg="#2D54E8"
                          color="white"
                          type="submit"
                          isLoading={isSubmitting}
                          loadingText="Saving..."
                          fontSize="14px"
                          _hover={{ bg: '#1a3db8' }}
                          px="6"
                        >
                          Save
                        </Button>
                      </HStack>
                    </VStack>
                  </Form>
                )}
              </Formik>
            </Box>
          </Flex>
        </Box>
      )}
    </>
  )
}
